import api from '../services/api';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import FlexiblePageWrapper from '../components/wrapper/FlexiblePageWrapper';
import Header from './../components/general/Header';
import Title from './../components/text/Title';
import Subtitle from '../components/text/Subtitle';
import Button from './../components/general/Button';
import TextInfo from '../components/text/TextInfo';
import InputTile from '../components/text/InputTile';
import TextAreaInputTile from '../components/text/TextAreaInputTile';
import SliderDifficulty from '../components/slider/SliderDifficulty';
import SliderQuantity from '../components/slider/SliderQuantity';
import SliderImportance from '../components/slider/SliderImportance';
import SliderUseful from '../components/slider/SliderUseful';
import SliderTrust from '../components/slider/SliderTrust';
import RadioQuestion from '../components/radio/RadioQuestion';
import RadioTable from '../components/radio/RadioTable';
import Footer from './../components/general/Footer';

function Survey() {
  const { lang, t } = useLanguage();

  const navigate = useNavigate();
  const [form, setForm] = useState({
    difficultyGame: 3,
    understandGame: 3,
    howMuchBias: 3,
    opinionBiasedSearch: null,
    responsibilityTable: [],
    negativeEffects: null,
    negativeEffectsReason: '',
    negativeBiasedResults: null,
    searchAtThatTime: '',
    whatDoBiasedSearch: null,
    trustTopSearch: 3,
    websitePayingSearch: null,
    AIFiltersOpinion: null,
    useAISearchEngines: 3,
    concernsAISummaryTable: [],
    communityNotes: null,
    viewUnfilteredResults: null,
    importanceReduceBias: 3,
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (key) => (e) => {
    const value = e?.target ? e.target.value : e; // support both events and raw values
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleTableChange = (formKey, rowLabels) => (selectedValsArr) => {
    const result = {};
    rowLabels.forEach((label, idx) => {
      result[label] = selectedValsArr[idx];
    });
    setForm((prev) => ({ ...prev, [formKey]: result }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Prevent multiple submissions
    if (submitting) return;
    setSubmitting(true);
    // Number the questions according to sections
    const sectionStarts = {
      1: 'difficultyGame',
      2: 'opinionBiasedSearch',
      3: 'negativeBiasedResults',
    };

    let currentSection = 1;
    let questionInSection = 1;
    const numberedForm = {};

    Object.entries(form).forEach(([key, value]) => {
      // If this key matches a section start, update section and reset question counter
      if (Object.values(sectionStarts).includes(key)) {
        currentSection = Number(Object.keys(sectionStarts).find((sec) => sectionStarts[sec] === key));
        questionInSection = 1;
      }
      const prefix = `${currentSection}.${questionInSection}`;
      numberedForm[`${prefix}.${key}`] = value;
      questionInSection++;
    });

    try {
      // Don't stringify - send the object directly
      const response = await api.publishSurveyResponse(numberedForm);

      navigate('/survey/end');
    } catch (err) {
      console.error('Submit failed:', err);
      alert('Failed to submit survey. Please try again.');
      setSubmitting(false);
    }
  };

  // ====== Quiz: 2. Autocomplete of searchbar ======

  const opinionBiasedSearchOpts = [
    { value: 'usefulBias', label: t('usefulBias') },
    { value: 'acceptableBias', label: t('acceptableBias') },
    { value: 'minimizeBias', label: t('minimizeBias') },
    { value: 'harmfulBias', label: t('harmfulBias') },
    { value: 'notSureBias', label: t('notSureBias') },
  ];

  const responsibilityRowKeys = ['TechCompany', 'Gov', 'Users', 'IndepOrg'];
  const responsibilityColKeys = [
    'noResponsibility',
    'lowResponsibility',
    'mediumResponsibility',
    'highResponsibility',
    'fullResponsibility',
  ];
  const responsibilityOpts = responsibilityRowKeys.map((rowKey) =>
    responsibilityColKeys.map((colKey, i) => ({
      value: { number: i, label: colKey },
      label: rowKey,
    })),
  );
  const responsibilityColLabels = responsibilityColKeys.map((key) => t(key));
  const responsibilityRowLabels = responsibilityRowKeys.map((key) => t(`Resp${key}`));

  const negativeEffectsOpts = [
    { value: 'yes', label: t('yes') },
    { value: 'no', label: t('no') },
  ];

  // ====== Quiz: 3. Search results and AI ======

  const negativeBiasedResultsOpts = [
    { value: 'yesFreq', label: t('yesFreq') },
    { value: 'sometimes', label: t('sometimes') },
    { value: 'rarely', label: t('rarely') },
    { value: 'never', label: t('never') },
  ];

  const whatDoBiasedSearchOpts = [
    { value: 'ignore', label: t('ignore') },
    { value: 'reformulate', label: t('reformulate') },
    { value: 'useOtherEngine', label: t('useOtherEngine') },
    { value: 'report', label: t('report') },
  ];

  const websitePayingSearchOpts = [
    { value: 'acceptablePractice', label: t('acceptablePractice') },
    { value: 'questionablePractice', label: t('questionablePractice') },
    { value: 'unfairPractice', label: t('unfairPractice') },
    { value: 'evilPractice', label: t('evilPractice') },
    { value: 'prohibitPractice', label: t('prohibitPractice') },
    { value: 'noOpinion', label: t('noOpinion') },
  ];

  const AIFiltersOpinionOpts = [
    { value: 'protectUsers', label: t('protectUsers') },
    { value: 'censorshipProblem', label: t('censorshipProblem') },
    { value: 'dependsTopic', label: t('dependsTopic') },
    { value: 'noOpinion', label: t('noOpinion') },
  ];

  const concernsAISummaryRowKeys = [
    'distortionInfo',
    'spreadMisinfo',
    'overrelianceAI',
    'lackTransparency',
    'lossDiversity',
    'privacyIssues',
    'manipulation',
  ];
  const concernsAISummaryColKeys = [
    'notImportant',
    'slightImportant',
    'moderateImportant',
    'important',
    'veryImportant',
  ];
  const concernsAISummaryOpts = concernsAISummaryRowKeys.map((rowKey) =>
    concernsAISummaryColKeys.map((colKey, i) => ({
      value: { number: i, label: colKey },
      label: rowKey,
    })),
  );
  const concernsAISummaryRowLabels = concernsAISummaryRowKeys.map((key) => t(key));
  const concernsAISummaryColLabels = concernsAISummaryColKeys.map((key) => t(key));

  const communityNotesOpts = [
    { value: 'yesAlw', label: t('yesAlw') },
    { value: 'yesSens', label: t('yesSens') },
    { value: 'noOpinion', label: t('noOpinion') },
    { value: 'no', label: t('noAnnot') },
  ];

  const viewUnfilteredResultsOpts = [
    { value: 'yesDef', label: t('yesDef') },
    { value: 'maybe', label: t('maybe') },
    { value: 'noMatter', label: t('noMatter') },
    { value: 'no', label: t('no') },
  ];

  return (
    <FlexiblePageWrapper>
      <Header text={t('headerTitle')} />
      <main className="w-full max-w-4xl mx-auto sm:px-4 pt-40 pb-28 flex flex-col items-center gap-6">
        <Title size="big" text={t('title')} />
        <Subtitle size="surveySectionBig" text={t('surveySubtitle')} />
        <br />
        <form onSubmit={handleSubmit} className="w-full px-8 flex flex-col items-center gap-20">
          <TextInfo
            hasTitle={true}
            title={t('surveyIntroductionTitle')}
            isContentText={false}
            content={
              <div className="text-base">
                <p className="font-bold">{t('surveyIntroduction1')}</p>
                <p className="mb-3 font-bold">{t('surveyIntroduction2')}</p>
                <ul className="list-disc pl-6 mb-3">
                  <li>{t('surveyIntroduction3')}</li>
                  <li>
                    <span className="font-bold">{t('surveyIntroduction4')}</span>
                  </li>
                  <li>{t('surveyIntroduction5')}</li>
                </ul>
                <p className="font-bold">{t('surveyIntroduction6')}</p>
              </div>
            }
          />

          <section className="w-full">
            <Subtitle size="surveySectionBig" text={t('surveySubtitle1')} color="red" />
            <br />
            <Subtitle size="surveyQuestionBig" text={t('understandGame')} />
            <SliderDifficulty
              value={form.understandGame}
              onChange={(v) => handleChange('understandGame')(v)}
            />

            <Subtitle size="surveyQuestionBig" text={t('difficultyGame')} />
            <SliderDifficulty
              value={form.difficultyGame}
              onChange={(v) => handleChange('difficultyGame')(v)}
            />

            <Subtitle size="surveyQuestionBig" text={t('howMuchBias')} />
            <SliderQuantity value={form.howMuchBias} onChange={(v) => handleChange('howMuchBias')(v)} />
          </section>

          <section className="w-full flex flex-col gap-10">
            <Subtitle size="surveySectionBig" text={t('surveySubtitle2')} color="green" />
            <fieldset>
              <Subtitle size="surveyQuestionBig" text={t('opinionBiasedSearch')} />
              <RadioQuestion
                name={'opinionBiasedSearch'}
                options={opinionBiasedSearchOpts}
                selected={form.opinionBiasedSearch}
                onChange={(v) => handleChange('opinionBiasedSearch')(v)}
              />
            </fieldset>

            <div className="overflow-auto max-w-full">
              <Subtitle size="surveyQuestionBig" text={t('responsibility')} />
              <div className={` py-4 ${lang === 'pt' ? 'w-[44em]' : 'w-[41em]'}`}>
                <RadioTable
                  options={responsibilityOpts}
                  colLabels={responsibilityColLabels}
                  rowLabels={responsibilityRowLabels}
                  onChange={(e) => handleTableChange('responsibilityTable', responsibilityRowKeys)(e)}
                />
              </div>
            </div>

            <fieldset>
              <Subtitle size="surveyQuestionBig" text={t('negativeEffects')} />
              <RadioQuestion
                name={'negativeEffects'}
                options={negativeEffectsOpts}
                selected={form.negativeEffects}
                onChange={(v) => handleChange('negativeEffects')(v)}
              />
            </fieldset>

            <fieldset>
              <Subtitle size="surveyQuestionBig" text={t('negativeEffectsReason')} />
              <TextAreaInputTile
                name="negativeEffectsReason"
                required={false}
                value={form.negativeEffectsReason}
                onChange={(e) => handleChange('negativeEffectsReason')(e)}
                maxLength={300}
              />
            </fieldset>
          </section>

          <section className="w-full flex flex-col gap-10">
            <Subtitle size="surveySectionBig" text={t('surveySubtitle3')} color="blue" />

            <fieldset>
              <Subtitle size="surveyQuestionBig" text={t('negativeBiasedResults')} />
              <RadioQuestion
                name={'negativeBiasedResults'}
                options={negativeBiasedResultsOpts}
                selected={form.negativeBiasedResults}
                onChange={(v) => handleChange('negativeBiasedResults')(v)}
              />
            </fieldset>

            <fieldset>
              <Subtitle size="surveyQuestionBig" text={t('searchAtThatTime')} />
              <InputTile
                type="survey"
                name="searchAtThatTime"
                required={false}
                value={form.searchAtThatTime}
                onChange={(e) => handleChange('searchAtThatTime')(e)}
                maxLength={300}
              />
            </fieldset>

            <fieldset>
              <Subtitle size="surveyQuestionBig" text={t('whatDoBiasedSearch')} />
              <RadioQuestion
                name={'whatDoBiasedSearch'}
                options={whatDoBiasedSearchOpts}
                selected={form.whatDoBiasedSearch}
                onChange={(v) => handleChange('whatDoBiasedSearch')(v)}
              />
            </fieldset>

            <fieldset>
              <Subtitle size="surveyQuestionBig" text={t('trustTopSearch')} />
              <SliderTrust value={form.trustTopSearch} onChange={(v) => handleChange('trustTopSearch')(v)} />
            </fieldset>

            <fieldset>
              <Subtitle size="surveyQuestionBig" text={t('websitePayingSearch')} />
              <RadioQuestion
                name={'websitePayingSearch'}
                options={websitePayingSearchOpts}
                selected={form.websitePayingSearch}
                onChange={(v) => handleChange('websitePayingSearch')(v)}
              />
            </fieldset>

            <fieldset>
              <Subtitle size="surveyQuestionBig" text={t('AIFiltersOpinion')} />
              <RadioQuestion
                name={'AIFiltersOpinion'}
                options={AIFiltersOpinionOpts}
                selected={form.AIFiltersOpinion}
                onChange={(v) => handleChange('AIFiltersOpinion')(v)}
              />
            </fieldset>

            <fieldset>
              <Subtitle size="surveyQuestionBig" text={t('useAISearchEngines')} />
              <SliderUseful
                value={form.useAISearchEngines}
                onChange={(v) => handleChange('useAISearchEngines')(v)}
              />
            </fieldset>

            <div className="overflow-auto max-w-full">
              <Subtitle size="surveyQuestionBig" text={t('concernsAISummary')} />
              <div className={` py-4 ${lang === 'pt' ? 'w-[36em]' : 'w-[34em]'}`}>
                <RadioTable
                  options={concernsAISummaryOpts}
                  colLabels={concernsAISummaryColLabels}
                  rowLabels={concernsAISummaryRowLabels}
                  onChange={(e) => handleTableChange('concernsAISummaryTable', concernsAISummaryRowKeys)(e)}
                />
              </div>
            </div>

            <fieldset>
              <Subtitle size="surveyQuestionBig" text={t('communityNotes')} />
              <span className="text-sm text-subtitle-light dark:text-subtitle-dark italic">
                {t('communityNotesInfo')}
              </span>
              <RadioQuestion
                name={'communityNotes'}
                options={communityNotesOpts}
                selected={form.communityNotes}
                onChange={(v) => handleChange('communityNotes')(v)}
              />
            </fieldset>

            <fieldset>
              <Subtitle size="surveyQuestionBig" text={t('viewUnfilteredResults')} />
              <RadioQuestion
                name={'viewUnfilteredResults'}
                options={viewUnfilteredResultsOpts}
                selected={form.viewUnfilteredResults}
                onChange={(v) => handleChange('viewUnfilteredResults')(v)}
              />
            </fieldset>

            <fieldset>
              <Subtitle size="surveyQuestionBig" text={t('importanceReduceBias')} />
              <SliderImportance
                value={form.importanceReduceBias}
                onChange={(v) => handleChange('importanceReduceBias')(v)}
              />
            </fieldset>
          </section>

          <Button type="submit" text={t('submitAnswers')} disabled={submitting} />
          <br />
        </form>
      </main>
      <Footer />
    </FlexiblePageWrapper>
  );
}

export default Survey;
